/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Returns all portfolios that are eligible to trade the given symbol.
 *
 * Eligibility rules:
 * - If a portfolio is NOT part of a league - it is eligible.
 * - If a portfolio is part of a league WITHOUT a stock whitelist - it is eligible for all symbols.
 * - If a portfolio is part of a league WITH a stock whitelist - the symbol must exist in the whitelist.
 *
 * @param symbolName - The name/code of the symbol to validate.
 * @param portfolios - List of user portfolios to evaluate.
 * @returns An array of eligible portfolios (empty array if none are eligible).
 */
export function getEligiblePortfoliosForSymbol(
  symbolName?: string | null,
  portfolios?: any[] | null
) {
  if (!portfolios?.length || !symbolName) return [];

  const normalizedSymbol = symbolName.toUpperCase();

  return portfolios.filter((p) => isPortfolioEligible(normalizedSymbol, p));
}

/**
 * Determines whether at least one portfolio is eligible to trade the given symbol.
 *
 * @param symbolName - The name/code of the symbol to validate.
 * @param portfolios - List of user portfolios to evaluate.
 * @returns True if at least one eligible portfolio exists; otherwise false.
 */
export function hasEligiblePortfoliosForSymbol(
  symbolName?: string | null,
  portfolios?: any[] | null
): boolean {
  return getEligiblePortfoliosForSymbol(symbolName, portfolios).length > 0;
}

/**
 * Checks whether a single portfolio is eligible to trade the given symbol
 * based on league and whitelist rules.
 */
function isPortfolioEligible(symbolName: string, portfolio: any): boolean {
  return (
    !portfolio?.leagueId ||
    !portfolio?.league?.hasStockWhitelist ||
    portfolio?.leagueWhitelistedStocks?.some(
      (s: string | null | undefined) => s?.toUpperCase() === symbolName
    ) === true
  );
}
